import { useState } from 'react';
import { FaCalculator, FaTimes, FaCopy } from 'react-icons/fa';
import '../styles/FloatingButton.css';

export default function FloatingButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [monto, setMonto] = useState('100000');
  const [retencion, setRetencion] = useState('15.25');
  const [isFocused, setIsFocused] = useState(false);
  const [copiedValue, setCopiedValue] = useState(null);

  // Formatear con separadores de miles
  const formatNumber = (num) => {
    if (!num) return '';
    return Number(num).toLocaleString('es-CL');
  };

  // Copiar valor sin formato al portapapeles
  const copyToClipboard = async (value, label) => {
    try {
      // Redondear y convertir a string sin decimales
      const plainValue = Math.round(value).toString();
      await navigator.clipboard.writeText(plainValue);
      setCopiedValue(label);
      setTimeout(() => setCopiedValue(null), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  // Cálculo para "Valor Líquido" - partiendo del líquido deseado
  const calculoLiquido = () => {
    const liquido = parseFloat(monto) || 0;
    const porcentaje = parseFloat(retencion) / 100;
    const bruto = liquido / (1 - porcentaje);
    const retencionMonto = bruto - liquido;

    return {
      bruto: bruto,
      liquido: liquido,
      retencion: retencionMonto
    };
  };

  // Cálculo para "Valor Bruto" - partiendo del bruto de la boleta
  const calculoBruto = () => {
    const bruto = parseFloat(monto) || 0;
    const porcentaje = parseFloat(retencion) / 100;
    const retencionMonto = bruto * porcentaje;
    const liquido = bruto - retencionMonto;

    return {
      bruto: bruto,
      liquido: liquido,
      retencion: retencionMonto
    };
  };

  const formatCLP = (n) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(n || 0);
  };

  const resultadoLiquido = calculoLiquido();
  const resultadoBruto = calculoBruto();

  return (
    <>
      {/* Botón flotante */}
      <button
        className="floating-button"
        onClick={() => setIsOpen(true)}
        title="Calculadora de Honorarios"
      >
        <FaCalculator className="floating-button-icon" />
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="floating-modal-overlay" onClick={() => setIsOpen(false)}>
          <div className="floating-modal-boleteo" onClick={(e) => e.stopPropagation()}>
            {/* Close button */}
            <button className="boleteo-close" onClick={() => setIsOpen(false)}>
              <FaTimes />
            </button>

            {/* Input de monto */}
            <div className="boleteo-amount-section">
              <label>Indique el Monto</label>
              <div className="boleteo-amount-display">
                <div className="boleteo-retencion-badge" style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '2px 6px' }}>
                  <input
                    type="text"
                    value={retencion}
                    onChange={(e) => setRetencion(e.target.value.replace(/[^\d.]/g, ''))}
                    className="retencion-input"
                    style={{
                      width: '40px',
                      background: 'transparent',
                      border: 'none',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: 'inherit',
                      textAlign: 'center',
                      outline: 'none'
                    }}
                  />
                  <span>%</span>
                </div>
                <input
                  type="text"
                  value={isFocused ? monto : formatNumber(monto)}
                  onChange={(e) => setMonto(e.target.value.replace(/[^\d]/g, ''))}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="boleteo-amount-input"
                  placeholder="0"
                />
                <span className="boleteo-currency">CLP</span>
              </div>
              <div className="boleteo-amount-note">
                Ingrese el monto en Pesos Chilenos
              </div>
            </div>

            {/* Resultados */}
            <div className="boleteo-results">
              <h3 className="boleteo-results-title">Resultado</h3>

              <div className="boleteo-results-grid">
                {/* Valor Líquido - Calcula desde el líquido */}
                <div className="boleteo-result-card">
                  <div className="boleteo-result-header">
                    <h4>Valor Líquido</h4>
                    <p>Cálculo basado en el monto líquido deseado</p>
                  </div>
                  <div className="boleteo-result-row">
                    <span>Monto de la Boleta</span>
                    <div className="boleteo-result-value-group">
                      <strong>{formatCLP(resultadoLiquido.bruto)}</strong>
                      <button
                        className={`boleteo-copy-btn ${copiedValue === 'liquido-bruto' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(resultadoLiquido.bruto, 'liquido-bruto')}
                        title="Copiar sin formato"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                  <div className="boleteo-result-row highlight">
                    <span>Monto a Recibir</span>
                    <div className="boleteo-result-value-group">
                      <strong className="amount-large">{formatCLP(resultadoLiquido.liquido)}</strong>
                      <button
                        className={`boleteo-copy-btn ${copiedValue === 'liquido-recibir' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(resultadoLiquido.liquido, 'liquido-recibir')}
                        title="Copiar sin formato"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                  <div className="boleteo-result-row">
                    <span>Retención SII ({retencion}%)</span>
                    <div className="boleteo-result-value-group">
                      <strong>{formatCLP(resultadoLiquido.retencion)}</strong>
                      <button
                        className={`boleteo-copy-btn ${copiedValue === 'liquido-retencion' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(resultadoLiquido.retencion, 'liquido-retencion')}
                        title="Copiar sin formato"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Valor Bruto - Calcula desde el bruto */}
                <div className="boleteo-result-card">
                  <div className="boleteo-result-header">
                    <h4>Valor Bruto</h4>
                    <p>Cálculo basado en el monto bruto de la boleta</p>
                  </div>
                  <div className="boleteo-result-row">
                    <span>Monto de la Boleta</span>
                    <div className="boleteo-result-value-group">
                      <strong>{formatCLP(resultadoBruto.bruto)}</strong>
                      <button
                        className={`boleteo-copy-btn ${copiedValue === 'bruto-bruto' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(resultadoBruto.bruto, 'bruto-bruto')}
                        title="Copiar sin formato"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                  <div className="boleteo-result-row highlight">
                    <span>Monto a Recibir</span>
                    <div className="boleteo-result-value-group">
                      <strong className="amount-large">{formatCLP(resultadoBruto.liquido)}</strong>
                      <button
                        className={`boleteo-copy-btn ${copiedValue === 'bruto-recibir' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(resultadoBruto.liquido, 'bruto-recibir')}
                        title="Copiar sin formato"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                  <div className="boleteo-result-row">
                    <span>Retención SII ({retencion}%)</span>
                    <div className="boleteo-result-value-group">
                      <strong>{formatCLP(resultadoBruto.retencion)}</strong>
                      <button
                        className={`boleteo-copy-btn ${copiedValue === 'bruto-retencion' ? 'copied' : ''}`}
                        onClick={() => copyToClipboard(resultadoBruto.retencion, 'bruto-retencion')}
                        title="Copiar sin formato"
                      >
                        <FaCopy />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}